/**
 * Non-cryptographic hashing used by every structure in this library.
 *
 * We implement MurmurHash3 (x86, 32-bit) — a fast, well-distributed hash with
 * published test vectors, so its correctness is easy to pin down. From it we
 * derive a 64-bit hash (needed by HyperLogLog and MinHash) by composing two
 * independently-seeded 32-bit hashes. Composing two good independent 32-bit
 * hashes into a 64-bit value is a standard, well-behaved technique for these
 * data structures (it is exactly the Kirsch–Mitzenmacher double-hashing idea).
 */

export type Key = string | Uint8Array;

const encoder = new TextEncoder();

/** Encode a key to bytes. Strings are encoded as UTF-8. */
export function toBytes(key: Key): Uint8Array {
  return typeof key === "string" ? encoder.encode(key) : key;
}

/** MurmurHash3 finalizer — mixes the bits of a 32-bit value. */
export function fmix32(h: number): number {
  h = (h ^ (h >>> 16)) >>> 0;
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h >>> 0;
}

/**
 * MurmurHash3 x86_32. Returns an unsigned 32-bit integer.
 * Matches the reference implementation's published test vectors.
 */
export function murmur3_32(key: Key, seed = 0): number {
  const data = toBytes(key);
  const len = data.length;
  const c1 = 0xcc9e2d51;
  const c2 = 0x1b873593;
  let h = seed >>> 0;

  const nblocks = len >>> 2;
  for (let i = 0; i < nblocks; i++) {
    const j = i << 2;
    let k =
      (data[j] | (data[j + 1] << 8) | (data[j + 2] << 16) | (data[j + 3] << 24)) >>> 0;
    k = Math.imul(k, c1) >>> 0;
    k = ((k << 15) | (k >>> 17)) >>> 0;
    k = Math.imul(k, c2) >>> 0;
    h = (h ^ k) >>> 0;
    h = ((h << 13) | (h >>> 19)) >>> 0;
    h = (Math.imul(h, 5) + 0xe6546b64) >>> 0;
  }

  // Tail — the remaining 1..3 bytes.
  let k1 = 0;
  const tail = nblocks << 2;
  const rem = len & 3;
  if (rem === 3) k1 ^= data[tail + 2] << 16;
  if (rem >= 2) k1 ^= data[tail + 1] << 8;
  if (rem >= 1) {
    k1 ^= data[tail];
    k1 = Math.imul(k1 >>> 0, c1) >>> 0;
    k1 = ((k1 << 15) | (k1 >>> 17)) >>> 0;
    k1 = Math.imul(k1, c2) >>> 0;
    h = (h ^ k1) >>> 0;
  }

  h = (h ^ len) >>> 0;
  return fmix32(h);
}

/**
 * A 64-bit hash, returned as an unsigned bigint in [0, 2^64).
 * Composed from two independently-seeded 32-bit MurmurHash3 values.
 */
export function hash64(key: Key, seed = 0): bigint {
  const bytes = toBytes(key);
  const hi = murmur3_32(bytes, seed >>> 0);
  const lo = murmur3_32(bytes, (seed ^ 0x9e3779b1) >>> 0);
  return (BigInt(hi) << 32n) | BigInt(lo);
}

/**
 * Two independent 32-bit hashes of the same key. Handy for the
 * Kirsch–Mitzenmacher scheme g_i(x) = h1 + i*h2 used by Bloom-style filters,
 * which needs only two hash evaluations regardless of how many probes we take.
 */
export function twoHashes(key: Key, seed = 0): [number, number] {
  const bytes = toBytes(key);
  const h1 = murmur3_32(bytes, seed >>> 0);
  const h2 = murmur3_32(bytes, (h1 ^ 0x9e3779b1) >>> 0);
  return [h1, h2];
}
